import React, {
  useContext,
} from 'react';
import {
  Row,
} from '@tanstack/react-table';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  EncounterFinancialDetailsDTO,
} from 'dtos';
import Button from 'pages/Dashboard/components/Button';
import {
  formatDate,
} from 'utils/date';
import {
  formatMoney,
  roundNumber,
} from '@populateui/utils';
import {
  AllocationContext,
} from 'views/EMR/Billing/EncountersAllocation';

type TotalDueCellProps = {
  row: Row<EncounterFinancialDetails>;
};

function TotalDueCell({ row }: TotalDueCellProps) {
  const { allocated } = useContext(AllocationContext);
  const { allocated: allocationAmount = 0 } = (allocated ?? []).find(
    ({ encounterId }) => encounterId === Number(row.id),
  ) ?? {};

  return formatMoney(
    roundNumber((row.original.financials?.totalDue ?? 0)
      - allocationAmount),
  );
}

type TotalPaidCellProps = {
  row: {
    id: string;
    original: EncounterFinancialDetails;
  };
};

function TotalPaidCell({ row }: TotalPaidCellProps) {
  const { allocated } = useContext(AllocationContext);
  const id = Number(row.id);
  const { allocated: amount = 0 } = (allocated ?? []).find(
    ({ encounterId }) => encounterId === id,
  ) ?? { allocated: 0 };

  return formatMoney((row.original?.financials?.totalPaid ?? 0) + amount);
}

type AllocateCellProps = {
  row: Row<EncounterFinancialDetails>;
};

function AllocateCell({ row }: AllocateCellProps) {
  const { toggle, allocatedEncounters, remainder } = useContext(AllocationContext);
  const isAllocated = allocatedEncounters.has(Number(row.id));

  return (
    <Button
      variant="text"
      color="primary"
      disabled={!isAllocated && remainder === 0}
      onClick={() => { toggle(row.original); }}
    >
      {isAllocated ? 'Un-allocate' : 'Allocate'}
    </Button>
  );
}

type AllocatedAmountCellProps = {
  row: Row<EncounterFinancialDetails>;
};

function AllocatedAmountCell({ row }: AllocatedAmountCellProps) {
  const { allocated, isAuto } = useContext(AllocationContext);
  const id = Number(row.id);
  const { allocated: amount = 0 } = (allocated ?? []).find(
    ({ encounterId }) => encounterId === id,
  ) ?? { allocated: 0 };

  return formatMoney(isAuto
    ? (row.original?.financials?.allocated ?? 0)
    : amount);
}

export type EncounterFinancialDetails = EncounterFinancialDetailsDTO & {
  allocated?: number;
};

const encounter: ColumnDef<EncounterFinancialDetails> = {
  id: 'encounter',
  accessorKey: 'encounterId',
  columnTitle: 'Encounter',
  meta: { autoSized: true },
  minSize: 80,
  cell: ({ row }) => (
    <div className="gap-px flex flex-col font-semibold max-w-max">
      <span className="text-gray-500">
        {formatDate(row.original?.encounterDate ?? '')}
      </span>
      <small className="truncate inline-block max-w-fit">
        {row.original?.visitType ?? '--'}
      </small>
    </div>
  ),
};

const copayBilled: ColumnDef<EncounterFinancialDetails> = {
  id: 'copayBilled',
  accessorKey: 'copayBilled',
  size: 80,
  minSize: 80,
  className: 'font-semibold',
  columnTitle: (
    <>
      Copay
      <br />
      Billed
    </>
  ),
  cell: ({ row }) => formatMoney(row.original?.financials?.copayBilled ?? 0),
};

const copayPaid: ColumnDef<EncounterFinancialDetails> = {
  id: 'copayPaid',
  accessorKey: 'Copay Paid',
  size: 80,
  minSize: 80,
  className: 'font-semibold',
  columnTitle: (
    <>
      CoPay
      <br />
      Paid
    </>
  ),
  cell: ({ row }) => formatMoney(row.original?.financials?.copayPaidAfterRefund ?? 0),
};

const servicesBilled: ColumnDef<EncounterFinancialDetails> = {
  id: 'servicesBilled',
  accessorKey: 'servicesBilled',
  className: 'font-semibold',
  size: 80,
  minSize: 80,
  columnTitle: (
    <>
      Services
      <br />
      Billed
    </>
  ),
  cell: ({ row }) => formatMoney(
    roundNumber(
      (row.original.financials?.serviceBilledWithoutCopay ?? 0)
      + (row.original.financials?.customServiceBilledAmount ?? 0),
    ),
  ),
};

const servicesPaid: ColumnDef<EncounterFinancialDetails> = {
  id: 'servicesPaid',
  accessorKey: 'servicesPaid',
  className: 'font-semibold',
  size: 80,
  minSize: 80,
  columnTitle: (
    <>
      Services
      <br />
      Paid
    </>
  ),
  cell: ({ row }) => formatMoney(
    (row.original.financials?.customServicePaid ?? 0)
    + (row.original.financials?.servicePaidWithoutCopay ?? 0),
  ),
};

const totalDue: ColumnDef<EncounterFinancialDetails> = {
  id: 'totalDue',
  accessorKey: 'totalDue',
  columnTitle: 'Due',
  className: 'font-semibold',
  size: 80,
  minSize: 80,
  cell: TotalDueCell,
};

const totalBilled: ColumnDef<EncounterFinancialDetails> = {
  id: 'totalBilled',
  accessorKey: 'totalBilled',
  columnTitle: 'Billed',
  className: 'font-semibold',
  size: 80,
  minSize: 80,
  cell: ({ row }) => formatMoney(row.original?.financials?.totalBilled ?? 0),
};

const totalPaid: ColumnDef<EncounterFinancialDetails> = {
  id: 'totalPaid',
  accessorKey: 'totalPaid',
  columnTitle: 'Paid',
  className: 'font-semibold',
  size: 80,
  minSize: 80,
  cell: TotalPaidCell,
};

const allocate: ColumnDef<EncounterFinancialDetails> = {
  id: 'actions',
  header: '',
  className: 'font-semibold justify-center',
  size: 80,
  minSize: 80,
  cell: AllocateCell,
};

const allocatedAmount: ColumnDef<EncounterFinancialDetails> = {
  id: 'allocated',
  columnTitle: 'Allocated',
  className: 'font-semibold',
  size: 80,
  minSize: 80,
  cell: AllocatedAmountCell,
};

const writeOff: ColumnDef<EncounterFinancialDetails> = {
  id: 'writeOff',
  columnTitle: (
    <>
      Total
      <br />
      Write Off
    </>
  ),
  className: 'font-semibold',
  size: 80,
  minSize: 80,
  cell: ({ row }) => formatMoney(
    row.original.financials?.totalEncounterPatientWriteOff ?? 0,
  ),
};

export const allocationColumns: ColumnDef<EncounterFinancialDetails>[] = [
  encounter,
  totalBilled,
  totalPaid,
  allocatedAmount,
  totalDue,
  allocate,
];
export const allocationPreviewColumns: ColumnDef<
  EncounterFinancialDetailsDTO>[] = allocationColumns.slice(0, 5);
export const totalBillReviewColumns: ColumnDef<EncounterFinancialDetails>[] = [
  encounter,
  copayBilled,
  copayPaid,
  servicesBilled,
  servicesPaid,
  writeOff,
  totalDue,
];
